import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogout } from '@field-tickets/shared';
import { useAuthStore } from '@/store/auth';
import { ticketRecoveryOwner } from '@/utils/ticketRecovery';
import { getAllQueuedTicketSaves, settlePendingTicketSnapshots } from '@/utils/offlineQueue';
import { memoryOnlyTicketEditsText, snapshotTicketForms } from '@/utils/ticketSessionSnapshot';
import { getAuthEpoch, isCurrentAuthEpoch } from '@/utils/authEpoch';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { showToast } from '@/components/Toast';

interface SignOutPrompt {
  epoch: number;
  memoryOnly: boolean;
  exportText: string;
  savedText?: string;
}

/** Every intentional sign-out checks the latest snapshots before server logout. */
export function useIntentionalSignOut(onCancel?: () => void) {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const owner = useAuthStore((state) => ticketRecoveryOwner(state.user));
  const [prompt, setPrompt] = useState<SignOutPrompt | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const frozenEpochRef = useRef<number | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (!busyRef.current && frozenEpochRef.current !== null) useAuthStore.getState().unfreezeTicketEditing(frozenEpochRef.current);
    };
  }, []);

  async function checkAndSignOut(epoch: number, savedText?: string, confirm = false) {
    if (busyRef.current || !isCurrentAuthEpoch(epoch)) return;
    if (ticketRecoveryOwner(useAuthStore.getState().user) !== owner) return;
    const alreadyFrozen = useAuthStore.getState().ticketEditingFrozenEpoch === epoch;
    busyRef.current = true;
    setBusy(true);
    frozenEpochRef.current = epoch;
    useAuthStore.getState().freezeTicketEditing(epoch);
    let awaitingConfirmation = false;
    try {
      // Confirmation revalidates the export, but cannot admit another copy of
      // the same frozen fields. Every pre-freeze admission is settled below.
      const snapshot = alreadyFrozen ? { dirtyCount: 0, durable: Promise.resolve(true) } : snapshotTicketForms();
      const [queued, durable] = await Promise.all([getAllQueuedTicketSaves(owner), snapshot.durable, settlePendingTicketSnapshots(owner)]);
      if (!mountedRef.current || !isCurrentAuthEpoch(epoch) || ticketRecoveryOwner(useAuthStore.getState().user) !== owner) return;
      const exportText = memoryOnlyTicketEditsText(owner);
      const memoryOnly = !durable || Boolean(exportText);
      if ((!confirm && (queued.length || snapshot.dirtyCount || memoryOnly)) ||
        (!durable && !savedText) || (exportText && exportText !== savedText)) {
        setPrompt({ epoch, memoryOnly, exportText });
        awaitingConfirmation = true;
        return;
      }
      setPrompt(null);
      try { await logoutMutation.mutateAsync(); } catch {
        showToast('Could not sign out. Your session and edits are still here. Try again.', 'error');
        return;
      }
      if (!isCurrentAuthEpoch(epoch) || ticketRecoveryOwner(useAuthStore.getState().user) !== owner) return;
      useAuthStore.getState().logout();
      navigate('/login', { replace: true });
    } finally {
      if (!awaitingConfirmation) useAuthStore.getState().unfreezeTicketEditing(epoch);
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function copyEdits(current: SignOutPrompt) {
    if (!isCurrentAuthEpoch(current.epoch) || ticketRecoveryOwner(useAuthStore.getState().user) !== owner) return;
    const exportText = memoryOnlyTicketEditsText(owner);
    try {
      await navigator.clipboard.writeText(exportText);
      if (isCurrentAuthEpoch(current.epoch)) setPrompt({ ...current, exportText, savedText: exportText });
    } catch {
      showToast('Clipboard unavailable. Select the edits below, save them, then acknowledge that you saved them.', 'warning');
    }
  }

  const saved = Boolean(prompt?.savedText && prompt.savedText === prompt.exportText);
  const dialog = prompt && isCurrentAuthEpoch(prompt.epoch) ? (
    <ConfirmDialog
      title="Sign out with unsent saves?"
      message={prompt.memoryOnly
        ? 'This browser could not store some edits. They are kept only in this tab and will be lost if it closes or reloads. Copy or save the edits below before signing out.'
        : 'You have queued ticket edits that have not been sent. Sign in again as the same user to recover them. Other accounts cannot see or send them. Edits expire after 30 days without changes.'}
      confirmLabel={prompt.memoryOnly && !saved ? 'Copy edits' : 'Sign out'}
      cancelLabel={prompt.memoryOnly ? 'Cancel sign-out' : 'Cancel'}
      checkboxLabel={prompt.memoryOnly ? "I've saved these edits" : undefined}
      checkboxDefaultChecked={saved}
      onCheckedChange={(checked) => setPrompt({ ...prompt, savedText: checked ? prompt.exportText : undefined })}
      onConfirm={() => {
        if (prompt.memoryOnly && !saved) void copyEdits(prompt);
        else void checkAndSignOut(prompt.epoch, prompt.savedText, true);
      }}
      onCancel={() => {
        useAuthStore.getState().unfreezeTicketEditing(prompt.epoch);
        setPrompt(null);
        onCancel?.();
      }}
    >
      {prompt.memoryOnly && (
        <textarea aria-label="Unsent ticket edits" className="form-input" rows={8} readOnly value={prompt.exportText}
          onFocus={(event) => event.currentTarget.select()} style={{ width: '100%' }} />
      )}
    </ConfirmDialog>
  ) : null;

  return { requestSignOut: () => checkAndSignOut(getAuthEpoch()), isPending: busy || logoutMutation.isPending, dialog };
}
